/**
 * People's own subscriptions (Claude, ChatGPT, GitHub Copilot, Google) on the machines that run their
 * work. A person sees theirs, finishes a sign-in a node is waiting on, keeps a headless token, or signs
 * out everywhere; administrators see who has which and where, never a secret, and choose what is allowed.
 * Every subscription is used only by its own vendor's tool: that is not a setting.
 */

import { HEADLESS_TOKEN, SUBSCRIPTION_LABEL, SUBSCRIPTION_VENDORS, subscriptionWindowWords, type HeadlessVendor, type SignIn, type Subscription, type SubscriptionSettings, type SubscriptionVendor } from '@aioe/shared';
import { time } from '@aioe/shared/wording/dateTime';
import { useState } from 'react';

import { listCount, ShowMore, useListSearch, usePagedList } from '../pagedList.tsx';
import { SearchBox } from './ui/SearchBox.tsx';
import { routes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { SignInAnswer, SignInLink } from './signIn.tsx';
import { Button, ConfirmDialog, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, Toggle } from './ui.tsx';

const isHeadless = (v: SubscriptionVendor): v is HeadlessVendor => v in HEADLESS_TOKEN;
const names = (xs: string[]) => (xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);

/** "Signed in on laptop and build-01 · Max · jp@example.com", in words. */
function whereSignedIn(s: Subscription | undefined): string {
  const on = s?.machines.filter((m) => m.state === 'signed-in') ?? [];
  const parts = [on.length ? `Signed in on ${names(on.map((m) => m.name))}` : s?.token ? 'Not signed in on a machine yet' : 'Not signed in anywhere'];
  if (s?.plan) parts.push(s.plan);
  if (s?.account) parts.push(s.account);
  const expired = s?.machines.filter((m) => m.state === 'expired').map((m) => m.name) ?? [];
  if (expired.length) parts.push(`expired on ${names(expired)}`);
  // The vendor's own usage windows (Claude's five hours and week, Copilot's premium requests), from the newest report.
  const windows = s?.machines.flatMap((m) => (m.windows ?? []).map((w) => subscriptionWindowWords(s.vendor, w))) ?? [];
  for (const w of [...new Set(windows)].slice(0, 3)) parts.push(w);
  return parts.join(' · ');
}

function PendingSignIn({ s, onChange }: { s: SignIn; onChange: () => void }) {
  const [error, setError] = useState<string>();
  const label = SUBSCRIPTION_LABEL[s.vendor];
  return (
    <div className="grid gap-2 rounded-lg bg-surface-secondary p-3">
      <div>
        <div className="font-medium">
          {s.machine} is waiting for you to sign in to {label.vendor}
        </div>
        <div className="text-xs text-muted">
          So it can run {label.runtime} for you. Open the sign-in{s.code ? ' and enter the code (it is copied for you)' : ''}; the machine finishes it. It waits until {time(s.expiresAt)}.
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <SignInLink label="Open sign-in" params={{ url: s.url, code: s.code }} />
        <Button size="sm" variant="ghost" onPress={() => void routes.cancelSignIn(s.id).then(onChange, (e: Error) => setError(e.message))}>
          Cancel
        </Button>
      </div>
      {s.wants && <SignInAnswer id={s.id} wants={s.wants} answered={s.answered} onSent={onChange} />}
      <ErrorNote error={error} />
    </div>
  );
}

function TokenForm({ vendor, onDone }: { vendor: HeadlessVendor; onDone: () => void }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    setError(undefined);
    try {
      await routes.putSubscriptionToken(vendor, value.trim());
      onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="grid gap-2">
      <label className="grid gap-1 text-xs text-muted">
        {HEADLESS_TOKEN[vendor].howTo} It is kept sealed and given only to {SUBSCRIPTION_LABEL[vendor].runtime}, for your own runs.
        <input className={`${inputClass} mono`} type="password" autoComplete="off" value={value} onChange={(e) => setValue(e.target.value)} placeholder={vendor === 'anthropic' ? 'sk-ant-oat01-…' : 'gho_… or github_pat_…'} />
      </label>
      <div className="flex gap-2">
        <Button size="sm" isPending={saving} isDisabled={value.trim().length < 20} onPress={() => void save()}>
          Keep it
        </Button>
        <Button size="sm" variant="ghost" onPress={onDone}>
          Cancel
        </Button>
      </div>
      <ErrorNote error={error} />
    </div>
  );
}

export function MySubscriptionsPanel() {
  const data = useLoad(() => routes.mySubscriptions(), []);
  const [adding, setAdding] = useState<HeadlessVendor>();
  const [signingOut, setSigningOut] = useState<SubscriptionVendor>();
  const [error, setError] = useState<string>();
  const d = data.data;
  const pending = d?.signIns.filter((s) => s.state === 'pending') ?? [];
  const run = (p: Promise<unknown>) => void p.then(() => data.reload(), (e: Error) => setError(e.message));
  return (
    <>
    <ListGroup
      id="subscriptions"
      title="Your subscriptions"
      description="Your own subscriptions, on your desktop and on the organisation's machines that run your work, each in your own profile. Each is used only by its own tool: Claude by Claude Code, ChatGPT by Codex. Your organisation sees which you have and where, never the sign-in itself. A machine that needs one of your sign-ins asks you here, in your inbox and on your phone."
    >
      {pending.map((s) => (
        <ListBlock key={s.id}>
          <PendingSignIn s={s} onChange={() => data.reload()} />
        </ListBlock>
      ))}
      {d
        ? SUBSCRIPTION_VENDORS.map((v) => {
            const s = d.subscriptions.find((x) => x.vendor === v);
            const allowed = d.settings.allowedVendors.includes(v);
            const label = SUBSCRIPTION_LABEL[v];
            const anywhere = Boolean(s?.token || s?.machines.some((m) => m.state === 'signed-in'));
            const actions = [
              allowed && isHeadless(v) && d.settings.headlessTokens && !s?.token && adding !== v ? (
                <Button key="keep" size="sm" variant="secondary" onPress={() => setAdding(v)}>
                  Keep a token
                </Button>
              ) : null,
              isHeadless(v) && s?.token ? (
                <Button key="remove" size="sm" variant="ghost" onPress={() => run(routes.removeSubscriptionToken(v))}>
                  Remove token
                </Button>
              ) : null,
              anywhere ? (
                <Button key="out" size="sm" variant="ghost" onPress={() => setSigningOut(v)}>
                  Sign out everywhere
                </Button>
              ) : null,
            ].filter(Boolean);
            return (
              <ListRow
                key={v}
                title={
                  <>
                    {label.vendor} <span className="font-normal text-muted">· used by {label.runtime}</span>
                  </>
                }
                description={
                  <>
                    {allowed ? whereSignedIn(s) : 'Not allowed on your organisation’s machines'}
                    {s?.token ? ` · token kept, ${s.token.releases === 0 ? 'not used yet' : `used ${s.token.releases === 1 ? 'once' : `${s.token.releases} times`}`}${s.token.lastReleasedAt ? `, last ${timeAgo(s.token.lastReleasedAt)}` : ''}` : ''}
                  </>
                }
                control={actions.length ? <>{actions}</> : undefined}
              >
                {adding === v && isHeadless(v) ? (
                  <TokenForm
                    vendor={v}
                    onDone={() => {
                      setAdding(undefined);
                      data.reload();
                    }}
                  />
                ) : null}
              </ListRow>
            );
          })
        : null}
      {data.error || error ? (
        <ListBlock>
          <ErrorNote error={data.error ?? error} />
        </ListBlock>
      ) : null}
    </ListGroup>
      <ConfirmDialog
        isOpen={!!signingOut}
        title={signingOut ? `Sign ${SUBSCRIPTION_LABEL[signingOut].vendor} out everywhere` : 'Sign out everywhere'}
        confirmLabel="Sign out"
        danger
        onCancel={() => setSigningOut(undefined)}
        onConfirm={() => {
          const v = signingOut;
          setSigningOut(undefined);
          if (v) run(routes.signOutEverywhere(v));
        }}
      >
        Machines sign out at their next check-in, and a kept token is removed.
      </ConfirmDialog>
    </>
  );
}

export function SubscriptionsAdmin() {
  // The signed-in list is searched and paged on the server; the settings come with the first page.
  const search = useListSearch();
  const list = usePagedList((cursor) => routes.subscriptionsPage({ q: search.q, cursor }), { q: search.q }, { keyOf: (s) => `${s.principal}/${s.vendor}` });
  const reg = { data: list.extra, error: list.error, reload: list.reload };
  const [error, setError] = useState<string>();
  const [revoking, setRevoking] = useState<Subscription>();
  if (!reg.data) return null;
  const { people, settings } = reg.data;
  const subscriptions = list.items;
  // A person this deployment has not seen sign in yet is named by the account a machine reported.
  const who = (p: string) => people[p]?.name ?? people[p]?.email ?? subscriptions.find((s) => s.principal === p && s.account)?.account ?? p;
  const save = (next: SubscriptionSettings) => void routes.setSubscriptionSettings(next).then(() => reg.reload(), (e: Error) => setError(e.message));
  const toggle = (v: SubscriptionVendor) => save({ ...settings, allowedVendors: settings.allowedVendors.includes(v) ? settings.allowedVendors.filter((x) => x !== v) : [...settings.allowedVendors, v] });
  const revoke = (s: Subscription) => {
    void routes.revokeSubscription(s.principal, s.vendor).then(() => reg.reload(), (e: Error) => setError(e.message));
  };
  const live = subscriptions;
  return (
    <>
      <ListGroup
        id="subscriptions-allowed"
        title="Allowed on the organisation’s machines"
        description="People’s own subscriptions on the organisation’s machines. A subscription only ever runs its own vendor’s tool, whatever is chosen here."
      >
        {SUBSCRIPTION_VENDORS.map((v) => (
          <ListRow
            key={v}
            title={SUBSCRIPTION_LABEL[v].vendor}
            description={`Used by ${SUBSCRIPTION_LABEL[v].runtime}.`}
            control={<Toggle label={`Allow ${SUBSCRIPTION_LABEL[v].vendor}`} checked={settings.allowedVendors.includes(v)} onChange={() => toggle(v)} />}
          />
        ))}
        <ListRow
          title="People may use their own subscription on shared nodes"
          description="Each person in their own profile on the node; nobody else’s run can use it."
          control={<Toggle label="People may use their own subscription on shared nodes" checked={settings.sharedNodes} onChange={(v) => save({ ...settings, sharedNodes: v })} />}
        />
        <ListRow
          title="People may keep a headless token here"
          description="Claude’s setup token or a GitHub token, sealed, given only to that person’s runs of its own tool."
          control={<Toggle label="People may keep a headless token here" checked={settings.headlessTokens} onChange={(v) => save({ ...settings, headlessTokens: v })} />}
        />
      </ListGroup>
      <ListGroup
        id="subscriptions-in-use"
        title="Signed in"
        count={listCount(live.length, { one: 'sign-in', many: 'sign-ins' }, { total: search.q ? undefined : reg.data.total, hasMore: list.hasMore, searched: search.q !== '' })}
        description="Whose subscription, which plan and where. You never see a sign-in or a token."
        actions={reg.data.total > 6 || search.q ? <SearchBox label="Search sign-ins" value={search.typed} onChange={search.setTyped} placeholder="Search sign-ins" className="w-40 max-w-full @lg:w-56" /> : undefined}
      >
        {live.length ? (
          live.map((s) => (
            <ListRow
              key={`${s.principal}/${s.vendor}`}
              title={
                <>
                  {who(s.principal)} <span className="font-normal text-muted">· {SUBSCRIPTION_LABEL[s.vendor].vendor}</span>
                </>
              }
              description={`${whereSignedIn(s.account === who(s.principal) ? { ...s, account: undefined } : s)}${s.token ? ' · keeps a token' : ''}`}
              control={
                <Button size="sm" variant="ghost" onPress={() => setRevoking(s)}>
                  Sign out everywhere
                </Button>
              }
            />
          ))
        ) : (
          <ListBlock className="text-muted">{search.q ? `Nobody matches "${search.q}".` : 'Nobody has signed a subscription in on the organisation’s machines yet.'}</ListBlock>
        )}
        {list.hasMore ? <ShowMore onPress={list.showMore} pending={list.loadingMore} /> : null}
        {reg.error || error ? (
          <ListBlock>
            <ErrorNote error={reg.error ?? error} />
          </ListBlock>
        ) : null}
      </ListGroup>
      <ConfirmDialog
        isOpen={!!revoking}
        title={revoking ? `Sign out ${who(revoking.principal)}’s ${SUBSCRIPTION_LABEL[revoking.vendor].vendor}` : 'Sign out'}
        confirmLabel="Sign out"
        danger
        onCancel={() => setRevoking(undefined)}
        onConfirm={() => {
          const s = revoking;
          setRevoking(undefined);
          if (s) revoke(s);
        }}
      >
        Signs it out of every machine. A kept token is removed; they can sign in again.
      </ConfirmDialog>
    </>
  );
}
